import { useState, useRef } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  Code2, Smartphone, Globe, Database, Brain, ArrowRight, CheckCircle,
  ChevronDown, ChevronRight, Shield,
} from "lucide-react";

const SERVICES = [
  {
    icon: Code2,
    color: "from-blue-600 to-blue-700",
    title: "Custom Software Development",
    description: "Tailor-made enterprise software engineered to your exact workflows, scaling from startup MVPs to full-scale ERP platforms.",
    features: [
      "End-to-end product design & architecture",
      "Agile delivery with weekly demos",
      "Lifetime code ownership & source handoff",
      "Post-launch maintenance & SLA support",
    ],
    price: "₹2,00,000+",
    link: "/enterprise-solutions",
  },
  {
    icon: Globe,
    color: "from-indigo-600 to-indigo-700",
    title: "Web Development",
    description: "High-performance web applications and e-commerce storefronts built for speed, SEO, and conversion from day one.",
    features: [
      "React, Next.js & headless CMS builds",
      "Core Web Vitals optimised (90+ score)",
      "Payment gateway & multi-currency support",
      "Mobile-first responsive UI/UX",
    ],
    price: "₹75,000+",
    link: "/ecommerce-development",
  },
  {
    icon: Smartphone,
    color: "from-violet-600 to-violet-700",
    title: "Mobile App Development",
    description: "Cross-platform and native mobile apps for iOS & Android that deliver a polished, consumer-grade experience.",
    features: [
      "Flutter & React Native cross-platform",
      "Native Swift / Kotlin when performance demands",
      "App Store & Play Store submission support",
      "Push notifications, offline mode & analytics",
    ],
    price: "₹1,50,000+",
    link: "/mobile-app-development",
  },
  {
    icon: Database,
    color: "from-cyan-600 to-cyan-700",
    title: "ERP & CRM Systems",
    description: "Unified business management platforms that replace spreadsheet chaos with real-time data, automation, and reporting.",
    features: [
      "Custom modules: HR, Finance, Inventory",
      "Role-based access & audit trails",
      "Third-party API & legacy system integration",
      "Interactive dashboards & BI reports",
    ],
    price: "₹2,50,000+",
    link: "/enterprise-solutions",
  },
  {
    icon: Shield,
    color: "from-emerald-600 to-emerald-700",
    title: "Digital Marketing",
    description: "Data-driven growth campaigns spanning SEO, paid media, and content marketing to build measurable pipeline.",
    features: [
      "Technical SEO audits & on-page optimisation",
      "Google Ads & Meta performance campaigns",
      "Content strategy & copywriting",
      "Monthly analytics reports with KPI tracking",
    ],
    price: "₹25,000/mo+",
    link: "/digital-marketing",
  },
  {
    icon: Brain,
    color: "from-rose-600 to-rose-700",
    title: "AI & Cloud Solutions",
    description: "Intelligent automation and cloud-native infrastructure that cut operational costs and unlock predictive insights.",
    features: [
      "LLM integration & custom AI model fine-tuning",
      "AWS / GCP cloud migration & architecture",
      "DevOps pipelines with CI/CD & Docker",
      "Real-time data pipelines & ML dashboards",
    ],
    price: "₹1,00,000+",
    link: "/contact",
  },
];

const STATS = [
  { value: "500+", label: "Projects Delivered" },
  { value: "50+", label: "Enterprise Clients" },
  { value: "25+", label: "Countries Served" },
  { value: "98%", label: "Client Satisfaction" },
];

const PROCESS_STEPS = [
  { step: "01", title: "Discovery", description: "We dive deep into your business objectives, map existing workflows, and define a clear scope so every rupee is spent purposefully." },
  { step: "02", title: "Architecture", description: "Our engineers design the system blueprint — tech stack selection, database schema, API contracts, and security protocols." },
  { step: "03", title: "Development", description: "Agile sprints with weekly demos, continuous integration, and code reviews ensure quality at every milestone." },
  { step: "04", title: "Launch", description: "Zero-downtime deployment, load testing, and a hypercare period so your go-live is smooth and stress-free." },
];

const TECH_CATEGORIES = [
  { title: "Frontend", color: "border-blue-500/30 bg-blue-950/30", dotColor: "bg-blue-500", items: ["React.js", "Next.js", "TypeScript", "Tailwind CSS"] },
  { title: "Backend", color: "border-indigo-500/30 bg-indigo-950/30", dotColor: "bg-indigo-500", items: ["Node.js", "Python", "Django / FastAPI", "PostgreSQL"] },
  { title: "Mobile", color: "border-violet-500/30 bg-violet-950/30", dotColor: "bg-violet-500", items: ["Flutter", "React Native", "iOS (Swift)", "Android (Kotlin)"] },
  { title: "Cloud & DevOps", color: "border-cyan-500/30 bg-cyan-950/30", dotColor: "bg-cyan-500", items: ["AWS / GCP", "Docker & Kubernetes", "CI/CD Pipelines", "Nginx"] },
];

const FAQS = [
  {
    q: "Which services does Vibhraad Technology provide?",
    a: "We cover the full software lifecycle — custom enterprise software, responsive web applications, cross-platform mobile apps, ERP & CRM systems, AI integrations, cloud infrastructure, and digital marketing. Whether you need a single landing page or a multi-tenant SaaS platform, we have a team for it.",
  },
  {
    q: "How is the project price determined?",
    a: "Pricing depends on scope complexity, number of integrations, design fidelity, and support tier. We provide a detailed fixed-price proposal after a free discovery call. Starting prices listed on this page represent the minimum engagement for each category.",
  },
  {
    q: "How long does a typical project take to deliver?",
    a: "A standard web application ships in 6-10 weeks. Mobile apps take 10-16 weeks. ERP/CRM systems typically span 3-6 months depending on module count. We work in two-week sprints so you see working software early and can course-correct at any stage.",
  },
  {
    q: "What happens after my project launches?",
    a: "Every engagement includes a 30-day hypercare period at no extra cost. Beyond that, we offer quarterly maintenance retainers, dedicated SLA support contracts, and feature roadmap sprints. You always retain full source code ownership regardless of the plan.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

function SectionWrapper({ children, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"} className={className}>
      {children}
    </motion.div>
  );
}

function ServiceCard({ service, index }) {
  const Icon = service.icon;
  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      className="group bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl border border-gray-100 hover:border-blue-100 transition-all duration-300 flex flex-col"
    >
      <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${service.color} mb-6 shadow-md`}>
        <Icon className="w-7 h-7 text-white" strokeWidth={1.75} />
      </div>
      <h3 className="text-xl font-black text-gray-900 mb-3">{service.title}</h3>
      <p className="text-gray-500 text-sm leading-relaxed mb-6">{service.description}</p>
      <ul className="space-y-2.5 mb-8 flex-1">
        {service.features.map((feat) => (
          <li key={feat} className="flex items-start gap-2.5 text-sm text-gray-600">
            <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <span>{feat}</span>
          </li>
        ))}
      </ul>
      <div className="flex items-center justify-between pt-6 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-widest font-semibold mb-0.5">Starting from</p>
          <p className="text-lg font-black text-gray-900">{service.price}</p>
        </div>
        <Link
          to={service.link}
          className="inline-flex items-center gap-1.5 text-sm font-bold text-blue-600 hover:text-blue-700 group-hover:gap-2.5 transition-all duration-200"
        >
          Learn More <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </motion.div>
  );
}

function FAQItem({ faq, index }) {
  const [open, setOpen] = useState(false);
  return (
    <motion.div variants={fadeUp} custom={index} className="border border-gray-200 rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left bg-white hover:bg-gray-50 transition-colors duration-200"
      >
        <span className="font-bold text-gray-900 text-base leading-snug">{faq.q}</span>
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.25 }} className="flex-shrink-0 text-blue-600">
          <ChevronDown className="w-5 h-5" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="px-6 pb-6 pt-1 text-gray-500 text-sm leading-relaxed border-t border-gray-100 bg-white">
              {faq.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

const ServicesPage = () => {
  const navigate = useNavigate();

  return (
    <div className="overflow-x-hidden">
      {/* 1. HERO */}
      <section className="relative bg-gray-950 pt-28 pb-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-40 -left-40 w-[600px] h-[600px] rounded-full bg-blue-600/10 blur-[120px]" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-indigo-600/10 blur-[100px]" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, #6b7280 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 bg-blue-600/15 border border-blue-500/30 text-blue-400 text-xs font-bold uppercase tracking-widest rounded-full px-4 py-2 mb-8"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-pulse" />
            500+ Projects Delivered
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white leading-[1.1] tracking-tight mb-6"
          >
            Complete Software Solutions{" "}
            <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">for Modern Enterprise</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            From concept to cloud — Vibhraad Technology engineers scalable digital products that drive measurable growth for businesses across 25+ countries.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-7 py-3.5 rounded-full shadow-lg shadow-blue-600/30 transition-all duration-200"
            >
              Get Free Consultation <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate("/pricing")}
              className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm px-7 py-3.5 rounded-full transition-all duration-200"
            >
              View Pricing <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* 2. STATS */}
      <section className="bg-gray-950 border-t border-white/5">
        <SectionWrapper className="max-w-6xl mx-auto px-6 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((stat, i) => (
            <motion.div key={stat.label} variants={fadeUp} custom={i} className="text-center">
              <p className="text-4xl font-black text-white mb-1">{stat.value}</p>
              <p className="text-gray-400 text-sm font-medium">{stat.label}</p>
            </motion.div>
          ))}
        </SectionWrapper>
      </section>

      {/* 3. SERVICES GRID */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionWrapper>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-3">What We Build</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-4">
                Six Service Areas.{" "}
                <span className="text-blue-600">One Trusted Partner.</span>
              </h2>
              <p className="text-gray-500 max-w-xl mx-auto leading-relaxed">
                Every engagement is backed by a dedicated team, transparent pricing, and a commitment to measurable outcomes.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICES.map((service, i) => (
                <ServiceCard key={service.title} service={service} index={i} />
              ))}
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* 4. PROCESS */}
      <section className="bg-white py-24">
        <div className="max-w-6xl mx-auto px-6">
          <SectionWrapper>
            <motion.div variants={fadeUp} className="text-center mb-16">
              <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-3">How We Work</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-gray-900 leading-tight mb-4">
                Our Proven <span className="text-blue-600">Delivery Process</span>
              </h2>
              <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                A repeatable four-phase framework refined over 500+ projects to keep your delivery on time and on budget.
              </p>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-6">
              {PROCESS_STEPS.map((step, i) => (
                <motion.div key={step.step} variants={fadeUp} custom={i} className="relative flex flex-col items-center text-center">
                  {i < PROCESS_STEPS.length - 1 && (
                    <div className="hidden lg:block absolute top-8 left-[calc(50%+2.5rem)] right-[calc(-50%+2.5rem)] h-px bg-gradient-to-r from-blue-300 to-blue-100 z-0" />
                  )}
                  <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-2xl bg-blue-600 text-white text-xl font-black shadow-lg shadow-blue-600/30 mb-5">
                    {step.step}
                  </div>
                  <h3 className="text-lg font-black text-gray-900 mb-2">{step.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-[220px]">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* 5. TECH STACK */}
      <section className="relative bg-gray-950 py-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[300px] bg-blue-600/8 blur-[100px] rounded-full" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, #6b7280 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-6">
          <SectionWrapper>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <p className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-3">Technology</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight mb-4">
                Built With{" "}
                <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">Industry-Leading Tools</span>
              </h2>
              <p className="text-gray-400 max-w-lg mx-auto leading-relaxed">
                We choose best-in-class technologies for each layer of your stack, never locking you into proprietary platforms.
              </p>
            </motion.div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {TECH_CATEGORIES.map((cat, i) => (
                <motion.div key={cat.title} variants={fadeUp} custom={i} className={`rounded-2xl border p-6 ${cat.color} backdrop-blur-sm`}>
                  <h4 className="text-white font-black text-base mb-4">{cat.title}</h4>
                  <ul className="space-y-2.5">
                    {cat.items.map((item) => (
                      <li key={item} className="flex items-center gap-2.5 text-gray-300 text-sm">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cat.dotColor}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="bg-white py-24">
        <div className="max-w-3xl mx-auto px-6">
          <SectionWrapper>
            <motion.div variants={fadeUp} className="text-center mb-14">
              <p className="text-blue-600 text-xs font-bold uppercase tracking-widest mb-3">FAQ</p>
              <h2 className="text-3xl sm:text-4xl font-black text-gray-900 leading-tight mb-4">Common Questions</h2>
              <p className="text-gray-500 leading-relaxed">Everything you need to know before we start building together.</p>
            </motion.div>
            <div className="space-y-4">
              {FAQS.map((faq, i) => (
                <FAQItem key={faq.q} faq={faq} index={i} />
              ))}
            </div>
          </SectionWrapper>
        </div>
      </section>

      {/* 7. CTA */}
      <section className="relative bg-gray-950 py-24 overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-blue-600/12 blur-[120px] rounded-full" />
          <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle, #6b7280 1px, transparent 1px)", backgroundSize: "28px 28px" }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <SectionWrapper>
            <motion.p variants={fadeUp} className="text-blue-400 text-xs font-bold uppercase tracking-widest mb-5">Let's Build Together</motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.1] mb-6">
              <span className="bg-gradient-to-r from-white via-blue-100 to-indigo-200 bg-clip-text text-transparent">Ready to Start Your Project?</span>
            </motion.h2>
            <motion.p variants={fadeUp} custom={2} className="text-gray-400 text-lg max-w-xl mx-auto leading-relaxed mb-10">
              Book a free 30-minute discovery call. No obligation — walk away with a clear project roadmap and ballpark estimate.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate("/contact")}
                className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-8 py-4 rounded-full shadow-xl shadow-blue-600/30 transition-all duration-200"
              >
                Schedule Free Consultation <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate("/pricing")}
                className="inline-flex items-center justify-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-bold text-sm px-8 py-4 rounded-full transition-all duration-200"
              >
                Explore Pricing <ChevronRight className="w-4 h-4" />
              </button>
            </motion.div>
          </SectionWrapper>
        </div>
      </section>
    </div>
  );
};

export default ServicesPage;
